import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "../api";
import { Mail, Loader2, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";

const black = "#111111";

export default function Signup() {
  const [form, setForm] = useState({
    person_name: "", phone: "", email: "", password: "", company_name: "", cui: "", address: "",
  });
  const [loading, setLoading] = useState(false);
  const [anafLoading, setAnafLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const fetchAnaf = async () => {
    const cui = (form.cui || "").trim();
    if (!cui) { toast.error("Introdu CUI-ul firmei"); return; }
    setAnafLoading(true);
    try {
      const { data } = await api.get(`/public/anaf/${encodeURIComponent(cui)}`);
      setForm((f) => ({
        ...f,
        company_name: data.company_name || f.company_name,
        address: data.address || f.address,
        phone: data.phone || f.phone,
      }));
      toast.success("Date preluate de la ANAF");
    } catch (err) {
      toast.error(apiError(err, "Nu am gasit firma la ANAF"));
    } finally {
      setAnafLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", form);
      setResult(data);
      toast.success("Cont creat. Foloseste linkul de activare.");
    } catch (err) {
      toast.error(apiError(err, "Inregistrare esuata"));
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(result.activation_url);
    toast.success("Link copiat");
  };

  const pageStyle = { backgroundColor: "#ffffff", color: black, fontFamily: "Arial, sans-serif", fontSize: "12px" };

  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={pageStyle}>
        <div className="w-full max-w-md text-center" data-testid="signup-success">
          <div className="w-14 h-14 rounded-md border border-gray-300 flex items-center justify-center mx-auto mb-6">
            <Mail className="w-7 h-7" style={{ color: black }} />
          </div>
          <h2 style={{ fontSize: "20px" }} className="font-bold">Contul a fost creat</h2>
          <p className="mt-2" style={{ fontSize: "13px" }}>
            Apasa butonul de mai jos pentru a activa contul si a te conecta acum.
          </p>

          <a
            href={result.activation_url}
            data-testid="signup-activate-now-button"
            className="mt-6 inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-md font-semibold"
            style={{ backgroundColor: "#059669", color: "#ffffff", fontSize: "14px", textDecoration: "none" }}
          >
            <CheckCircle2 className="w-4 h-4" /> Activeaza contul acum
          </a>

          <button
            type="button"
            onClick={copyLink}
            data-testid="signup-copy-link-button"
            className="mt-3 inline-flex items-center justify-center gap-2 w-full px-4 py-2 rounded-md border border-gray-300"
            style={{ fontSize: "13px", backgroundColor: "#ffffff", color: black }}
          >
            <Copy className="w-4 h-4" /> Copiaza linkul de activare
          </button>

          <p className="mt-5" style={{ fontSize: "12px", color: "#64748b" }}>
            Ti-am trimis linkul si pe email ({result.email}). Daca nu il vezi, verifica
            folderul spam sau foloseste butonul de mai sus.
          </p>
          <p className="mt-3" style={{ fontSize: "12px" }}>Subdomeniu rezervat: {result.slug}.yvola.eu</p>
        </div>
      </div>
    );
  }

  const inputCls = "w-full mb-4 px-3 py-2 rounded-md border border-gray-300 outline-none focus:border-gray-500";

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={pageStyle}>
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="signup-form">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-9 h-9 rounded-md border border-gray-300 flex items-center justify-center">
            <Mail className="w-5 h-5" style={{ color: black }} />
          </div>
          <span style={{ fontSize: "18px" }} className="font-bold">YVOLA</span>
        </div>
        <h2 style={{ fontSize: "20px" }} className="font-bold">Inregistrare firma</h2>
        <p className="mt-1 mb-8" style={{ fontSize: "13px" }}>Completeaza datele pentru contul de admin.</p>

        <div className="mb-4">
          <label className="block mb-1" style={{ fontSize: "13px" }}>CUI firma</label>
          <div className="flex gap-2">
            <input
              data-testid="signup-cui-input"
              type="text" value={form.cui} onChange={set("cui")}
              className="flex-1 px-3 py-2 rounded-md border border-gray-300 outline-none focus:border-gray-500"
              placeholder="RO12345678"
            />
            <button type="button" onClick={fetchAnaf} disabled={anafLoading}
              data-testid="signup-anaf-button"
              className="px-3 py-2 rounded-md text-white font-medium disabled:opacity-60 whitespace-nowrap"
              style={{ backgroundColor: black, fontSize: "13px" }}>
              {anafLoading ? "Se preia…" : "Preia date"}
            </button>
          </div>
          <p className="mt-1" style={{ fontSize: "11px", color: "#555" }}>
            Introdu CUI-ul si apasa Preia date pentru a completa automat datele firmei de la ANAF.
          </p>
        </div>

        {[
          { k: "company_name", label: "Denumire firma", type: "text", ph: "Firma SRL", req: true },
          { k: "address", label: "Adresa firma", type: "text", ph: "Adresa completa", req: false },
          { k: "person_name", label: "Nume persoana", type: "text", ph: "Ion Popescu", req: true },
          { k: "phone", label: "Telefon", type: "tel", ph: "07xx xxx xxx", req: false },
          { k: "email", label: "Email", type: "email", ph: "contact@firma.ro", req: true },
          { k: "password", label: "Parola", type: "password", ph: "parola", req: true },
        ].map((f) => (
          <div key={f.k}>
            <label className="block mb-1" style={{ fontSize: "13px" }}>{f.label}</label>
            <input
              data-testid={`signup-${f.k}-input`}
              type={f.type} required={f.req} value={form[f.k]} onChange={set(f.k)}
              className={inputCls}
              placeholder={f.ph}
            />
          </div>
        ))}

        <button
          data-testid="signup-submit-button"
          type="submit" disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md text-white font-medium disabled:opacity-60"
          style={{ backgroundColor: black, fontSize: "13px" }}
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Creeaza contul
        </button>
        <p className="mt-5 text-center" style={{ fontSize: "13px" }}>
          Ai deja cont? <Link to="/login" className="underline" data-testid="signup-to-login">Autentifica-te</Link>
        </p>
      </form>
    </div>
  );
}
